import { ReactNode, useMemo, useRef, useState } from "react";
import { Components } from "react-markdown";
import styled from "@emotion/styled";

import { Markdown } from "components/reports/builder_components/Markdown";
import Tooltip from "components/ui/tooltip/Tooltip";
import { BidReportEntry_Answer_Sources_Locations } from "lib/generated/app-service-gql/graphql";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import { useAiContext } from "lib/providers/AiContextProvider";
import { EventNames, useTracking } from "lib/tracking";
import { AiSourceDocumentType, AiSourceType } from "lib/types/graphQLEnums";
import {
  AiDocumentSource,
  AiNoticeSource,
  AiSource,
  AiWebsiteSource,
} from "../../../components/ai_chat/types";
import { CustomerDocumentDrawer } from "../../../components/customer_documents/CustomerDocumentDrawer";
import { BuyerDocumentDrawer } from "../../../components/document_details/BuyerDocumentDrawer";
import { opacityBlink } from "../../../styles/animations";
import { createReferenceLinkOpenedEventPayload } from "./sources/tracking";

type AnswerMarkdownProps = {
  answer: string;
  trackingLinkContext?: (guid: string) => void;
  sources?: AiSource[];
  isStreaming?: boolean;
  /**
   * Provide to override the default behaviour of opening a viewer
   * in a popout drawer e.g. because the cited document is already rendered in-page
   */
  onDocumentSourceClick?: (source: AiDocumentSource) => void;
  customComponents?: Components;
  /**
   * Opt-in: render internal `/notices/{guid}` and `/documents/{id}` markdown links
   * (e.g. the lead summary's "Lead created from [title](…)" line) as preview-opening
   * links — a notice slideout / document drawer — instead of navigating in a new tab.
   * Defaults to the new-tab behaviour for all other consumers.
   */
  previewInternalLinks?: boolean;
};

export function AnswerMarkdown({
  answer,
  trackingLinkContext,
  sources,
  isStreaming,
  onDocumentSourceClick,
  customComponents,
  previewInternalLinks = false,
}: AnswerMarkdownProps) {
  const { viewRecord: showNotice } = useRestricedViewNotice("Citation click");
  const { logEvent } = useTracking();
  const { resourceId, resourceType, bidId } = useAiContext();

  // State for document drawer when citation references a document
  const [openDocumentSource, setOpenDocumentSource] = useState<AiDocumentSource | null>(null);

  // Build lookup map of citationId -> source
  const citationMap = useMemo(() => {
    const map: Record<string, AiSource> = {};
    (sources || []).forEach((s) => {
      if (s.citationId) {
        map[s.citationId] = s;
      }
    });
    return map;
  }, [sources]);

  // Strip any leaked LLM envelope tags first (defensive — the backend parser is
  // meant to strip these, but if it falls back to `rawAnswer` they end up in the
  // persisted answer text), then transform `[N]` citation tokens into markdown links.
  const transformedAnswer = useMemo(() => {
    return answer
      .replace(/\[SOURCES\][\s\S]*?\[\/SOURCES\]/g, "")
      .replace(/\[THINKING\][\s\S]*?\[\/THINKING\]/g, "")
      .replace(/\[\/?ANSWER\]/g, "")
      .replace(/\[(\d+)\]/g, (match, id) => `[${match}](citation://${id})`);
  }, [answer]);

  const logReferenceLinkOpened = (source: AiSource) => {
    logEvent(
      EventNames.referenceLinkOpened,
      createReferenceLinkOpenedEventPayload({ source, resourceType, resourceId, bidId }),
    );
  };

  const handleCitationClick = (id: string) => {
    const source = citationMap[id];
    if (!source) return;

    // Tracking event matches other source clicks
    logReferenceLinkOpened(source);

    switch (source.type) {
      case "notice": {
        const noticeSource = source as AiNoticeSource;
        showNotice(noticeSource.id);
        break;
      }
      case "website": {
        const websiteSource = source as AiWebsiteSource;
        window.open(websiteSource.url, "_blank", "noopener,noreferrer");
        break;
      }
      case "document": {
        const documentSource = source as AiDocumentSource;
        if (documentSource.isDeleted) break;
        if (onDocumentSourceClick) {
          onDocumentSourceClick(documentSource);
        } else {
          setOpenDocumentSource(documentSource);
        }
        break;
      }
    }
  };

  const renderLink = ({ children, href }: { children?: ReactNode; href?: string }) => {
    if (href?.startsWith("citation://")) {
      const id = href.substring("citation://".length);
      const source = citationMap[id];
      const isDeleted = source?.type === "document" && (source as AiDocumentSource).isDeleted;
      const clickable = !!source && !isDeleted;

      if (!source) {
        return (
          <Citation
            clickable={false}
            isDeleted={false}
            hasSource={false}
            aria-label={`Citation ${id}`}
          >
            {children}
          </Citation>
        );
      }

      return (
        <Tooltip content={<SourceTooltipContent source={source} />} side="top" delayDuration={150}>
          <Citation
            clickable={clickable}
            isDeleted={isDeleted ?? false}
            hasSource={true}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (clickable) handleCitationClick(id);
            }}
            onKeyDown={(e) => {
              if (!clickable) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleCitationClick(id);
              }
            }}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={
              source ? (isDeleted ? `Source ${id} deleted` : `Open source ${id}`) : `Citation ${id}`
            }
          >
            {children}
          </Citation>
        </Tooltip>
      );
    }

    // Opt-in: route internal record links (the lead summary's "Lead created from
    // [title](/documents|notices/…)" line) to the same in-page previews the citations use.
    const internalLink = previewInternalLinks && href ? parseInternalRecordLink(href) : null;
    if (internalLink) {
      const openPreview = () => {
        if (internalLink.kind === "notice") {
          logReferenceLinkOpened({
            type: AiSourceType.Notice,
            id: internalLink.guid,
            title: linkText(children),
          });
          showNotice(internalLink.guid);
          return;
        }
        // Reuse a cited source for the same document when present (correct type + location);
        // otherwise synthesise a minimal buyer-document source — these "Create lead from
        // document" entry points are always buyer documents.
        const cited = (sources || []).find(
          (s): s is AiDocumentSource => s.type === "document" && s.id === internalLink.id,
        );
        const documentSource: AiDocumentSource = cited ?? {
          type: AiSourceType.Document,
          id: internalLink.id,
          title: linkText(children),
          documentType: AiSourceDocumentType.BuyerDocument,
          chunkNumber: 0,
          locations: [],
        };
        if (documentSource.isDeleted) return;
        logReferenceLinkOpened(documentSource);
        if (onDocumentSourceClick) {
          onDocumentSourceClick(documentSource);
        } else {
          setOpenDocumentSource(documentSource);
        }
      };

      return (
        <PreviewLink
          href={href}
          // Preserve the href so cmd/ctrl/shift-click and middle-click still open the full
          // page in a new tab; a plain click opens the in-page preview instead of navigating.
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            openPreview();
          }}
        >
          {children}
        </PreviewLink>
      );
    }

    // Fallback to default link rendering for normal http/https links
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };

  // react-markdown treats a new component as a new element type and rebuilds every link, which
  // drops a citation's pending tooltip whenever this re-renders; one component reads the latest.
  const renderLinkRef = useRef(renderLink);
  renderLinkRef.current = renderLink;
  const markdownComponents = useMemo(
    () => ({
      ...customComponents,
      a: function CitationOrLink(props: { children?: ReactNode; href?: string }) {
        return renderLinkRef.current(props);
      },
    }),
    [customComponents],
  );

  return (
    <>
      <StreamingMarkdown
        text={transformedAnswer}
        trackingLinkContext={trackingLinkContext}
        targetType="new-tab"
        customComponents={markdownComponents}
        isStreaming={!!isStreaming}
        allowInternalLinks={previewInternalLinks}
      />
      {sources && (
        <>
          {/* TODO potentially refactor into a context or similar as these are rendered in the ai sources panel as well */}
          {openDocumentSource && openDocumentSource.documentType !== "BUYER_DOCUMENT" && (
            <CustomerDocumentDrawer
              source={{
                documentId: openDocumentSource.id,
                fileName: openDocumentSource.title,
                chunkNumber: openDocumentSource.chunkNumber,
                locations: (openDocumentSource.locations ||
                  []) as BidReportEntry_Answer_Sources_Locations[],
              }}
              onClose={() => setOpenDocumentSource(null)}
              isOpen={!!openDocumentSource}
            />
          )}
          {openDocumentSource && openDocumentSource.documentType === "BUYER_DOCUMENT" && (
            <BuyerDocumentDrawer
              isOpen={!!openDocumentSource}
              onClose={() => setOpenDocumentSource(null)}
              documentId={openDocumentSource.id}
              source={{
                documentId: openDocumentSource.id,
                fileName: openDocumentSource.title,
                chunkNumber: openDocumentSource.chunkNumber,
                locations: (openDocumentSource.locations ||
                  []) as BidReportEntry_Answer_Sources_Locations[],
              }}
            />
          )}
        </>
      )}
    </>
  );
}

type InternalRecordLink = { kind: "notice"; guid: string } | { kind: "document"; id: string };

// Matches the internal links the lead summary seeds into its description, tolerating an
// origin prefix (some callers build absolute notice URLs) and trailing query/hash.
function parseInternalRecordLink(href: string): InternalRecordLink | null {
  let pathname = href;
  if (/^https?:\/\//i.test(href)) {
    try {
      pathname = new URL(href).pathname;
    } catch {
      return null;
    }
  }
  const noticeMatch = pathname.match(/^\/notices\/([^/?#]+)/);
  if (noticeMatch) return { kind: "notice", guid: noticeMatch[1] };
  const documentMatch = pathname.match(/^\/documents\/([^/?#]+)/);
  if (documentMatch) return { kind: "document", id: documentMatch[1] };
  return null;
}

// Best-effort plain-text extraction from a link's children (used for the source title).
function linkText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) {
    return children.filter((c): c is string => typeof c === "string").join("");
  }
  return "";
}

type SourceTooltipContentProps = { source?: AiSource };
function SourceTooltipContent({ source }: SourceTooltipContentProps) {
  if (!source) return <TooltipBox>No source info</TooltipBox>;
  if (source.type === "notice") {
    return (
      <TooltipBox>
        <TooltipTitle>{source.title}</TooltipTitle>
        <TooltipType>Notice</TooltipType>
      </TooltipBox>
    );
  }
  if (source.type === "website") {
    return (
      <TooltipBox>
        <TooltipTitle>{source.title}</TooltipTitle>
        <TooltipType>Website</TooltipType>
        <TooltipUrl>{source.url}</TooltipUrl>
      </TooltipBox>
    );
  }
  if (source.type === "document") {
    if (source.isDeleted) {
      return (
        <TooltipBox>
          <TooltipTitle>{source.title}</TooltipTitle>
          <TooltipType>Document (deleted)</TooltipType>
        </TooltipBox>
      );
    }
    return (
      <TooltipBox>
        <TooltipTitle>{source.title}</TooltipTitle>
        <TooltipType>Document</TooltipType>
        {source.filePath && <TooltipUrl>{source.filePath}</TooltipUrl>}
      </TooltipBox>
    );
  }
  return <TooltipBox>Unknown source</TooltipBox>;
}

const TooltipBox = styled.div({
  minWidth: 180,
  maxWidth: 320,
});
const TooltipTitle = styled.div({ fontWeight: 600, marginBottom: 2, fontSize: 14 });
const TooltipType = styled.div(({ theme }) => ({
  color: theme.colors.sysTextInactive,
  fontSize: 12,
  marginBottom: 2,
}));
const TooltipUrl = styled.div(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  fontSize: 12,
  wordBreak: "break-all",
}));

const Citation = styled.span<{ clickable: boolean; isDeleted: boolean; hasSource: boolean }>(
  ({ clickable, isDeleted, hasSource, theme }) => ({
    cursor: clickable ? "pointer" : "default",
    color: clickable
      ? theme.colors.sysPrimaryDefault
      : isDeleted
        ? theme.colors.sysTextInactive
        : hasSource
          ? "inherit"
          : theme.colors.sysTextInactive,
    textDecoration: clickable ? "underline" : "none",
    outline: "none",
    verticalAlign: "baseline",
    marginLeft: 2,
    marginRight: 2,
  }),
);

const PreviewLink = styled.a(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  textDecoration: "underline",
  cursor: "pointer",
}));

const StreamingMarkdown = styled(Markdown)<{ isStreaming: boolean }>(({ theme, isStreaming }) => {
  const caretStyles = {
    content: '""',
    display: "inline-block",
    width: 7,
    height: "1em",
    marginLeft: 2,
    verticalAlign: "-0.1em",
    background: theme.colors.sysTextInactive,
    borderRadius: 2,
    opacity: isStreaming ? 1 : 0,
    animation: isStreaming ? `${opacityBlink} 1.05s step-end infinite` : "none",
    transition: "opacity 160ms ease",
  } as const;

  return {
    // Attach the caret to the end of the last rendered block.
    "& > p:last-child::after": caretStyles,
    "& > ul:last-child > li:last-child::after": caretStyles,
    "& > ol:last-child > li:last-child::after": caretStyles,
    "& > pre:last-child code::after": caretStyles,
    "& > blockquote:last-child p:last-child::after": caretStyles,
  };
});
